import { useEffect, useState } from "react";
import { Target, Loader2, ExternalLink } from "lucide-react";
import { Button } from "../ui/button";
import { adminAPI } from "../../lib/api";

const Stat = ({ label, value, tone = "text-white", testId }) => (
  <div className="rounded-lg bg-black/20 border border-white/5 px-3 py-2">
    <p className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className={`text-lg font-semibold ${tone}`} data-testid={testId}>{value}</p>
  </div>
);

const WeekRow = ({ w }) => (
  <tr className="border-t border-white/5 text-xs">
    <td className="py-1.5 pr-3 text-muted-foreground">{w.week_start} – {w.week_end}</td>
    <td className="pr-3">{w.clips_checked}</td>
    <td className="pr-3">{w.clips_missed}</td>
    <td className="pr-3 text-emerald-400">{w.fixed_by_retry}</td>
    <td className="pr-3 text-amber-400">{w.still_missing}</td>
    <td className="pr-3">{w.extra_clips} (~${w.extra_cost_usd.toFixed(2)})</td>
  </tr>
);

const AffectedVideo = ({ v, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(v.id)}
    className="w-full text-left flex items-start gap-2 p-2 rounded-lg hover:bg-white/5 transition-colors"
    data-testid={`action-report-video-${v.id}`}
  >
    <ExternalLink className="w-3.5 h-3.5 mt-0.5 text-cyan-400 shrink-0" />
    <span className="min-w-0 flex-1">
      <span className="block text-sm text-white truncate">{v.title || v.id}</span>
      <span className="block text-xs text-muted-foreground truncate">
        {v.user_email || "unknown user"} · missed: {v.missed.join("; ")}
      </span>
    </span>
    <span className={`text-xs shrink-0 ${v.fixed ? "text-emerald-400" : "text-amber-400"}`}>
      {v.fixed ? "Fixed by retry" : "Still missing"}
    </span>
  </button>
);

export function ActionSuccessReportCard({ onOpenVideo }) {
  const [report, setReport] = useState(null);
  const [error, setError] = useState(false);

  const load = async () => {
    setError(false);
    try {
      const { data } = await adminAPI.getActionSuccessReport(4);
      setReport(data);
    } catch {
      setError(true);
    }
  };

  useEffect(() => { load(); }, []);

  const week = report?.weeks?.[0];
  return (
    <div data-testid="action-success-report-card" className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-4">
      <div className="flex items-center gap-2">
        <Target className="w-4 h-4 text-cyan-400" />
        <span className="text-sm font-medium">Requested actions – last 7 days</span>
        <span className="text-xs text-muted-foreground">(hidden from users)</span>
        {error && <Button size="sm" variant="ghost" className="ml-auto" onClick={load} data-testid="action-report-retry">Retry</Button>}
      </div>
      {!report && !error && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      {error && <p className="text-xs text-muted-foreground">Couldn't load the report.</p>}
      {week && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            <Stat label="Scenes checked" value={week.clips_checked} testId="action-report-checked" />
            <Stat label="Missed an action" value={week.clips_missed} testId="action-report-missed" />
            <Stat label="Fixed by retry" value={week.fixed_by_retry} tone="text-emerald-400" testId="action-report-fixed" />
            <Stat label="Still missing" value={week.still_missing} tone="text-amber-400" testId="action-report-still-missing" />
            <Stat label="Extra cost" value={`${week.extra_clips} clips · ~$${week.extra_cost_usd.toFixed(2)}`} testId="action-report-extra-cost" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Last 4 weeks</p>
              <table className="w-full" data-testid="action-report-weeks">
                <thead>
                  <tr className="text-[11px] text-muted-foreground text-left">
                    <th className="pr-3 font-normal">Week</th><th className="pr-3 font-normal">Checked</th>
                    <th className="pr-3 font-normal">Missed</th><th className="pr-3 font-normal">Fixed</th>
                    <th className="pr-3 font-normal">Still missing</th><th className="pr-3 font-normal">Extra clips</th>
                  </tr>
                </thead>
                <tbody>{report.weeks.map((w) => <WeekRow key={w.week_start} w={w} />)}</tbody>
              </table>
            </div>
            <div data-testid="action-report-top-missed">
              <p className="text-xs text-muted-foreground mb-1">Most often missed</p>
              {report.top_missed.length === 0 && <p className="text-xs text-muted-foreground">Nothing missed in this period.</p>}
              {report.top_missed.map((t) => (
                <p key={t.action} className="text-sm text-white flex justify-between gap-2">
                  <span className="truncate first-letter:uppercase">{t.action}</span>
                  <span className="text-muted-foreground">{t.count}×</span>
                </p>
              ))}
            </div>
          </div>
          <div data-testid="action-report-affected">
            <p className="text-xs text-muted-foreground mb-1">Affected videos</p>
            {report.affected_videos.length === 0 && <p className="text-xs text-muted-foreground">No affected videos.</p>}
            <div className="max-h-56 overflow-y-auto">
              {report.affected_videos.map((v) => <AffectedVideo key={v.id} v={v} onOpen={onOpenVideo} />)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
